<template>
  <div class="quiz-player">
    <!-- Quiz Start Screen -->
    <div v-if="!started && !loading" class="quiz-start-screen">
      <div class="quiz-info-card">
        <h3>{{ quizData?.quiz?.title || 'Quiz' }}</h3>
        <p v-if="quizData?.quiz?.description">{{ quizData.quiz.description }}</p>
        
        <div class="quiz-stats">
          <div class="stat-item">
            <i class="fa fa-question-circle"></i>
            <span>{{ quizData?.questionsCount || 0 }} questões</span>
          </div>
          <div class="stat-item" v-if="quizData?.attemptsRemaining !== undefined">
            <i class="fa fa-redo"></i>
            <span>{{ quizData.attemptsRemaining }} tentativa(s) restante(s)</span>
          </div>
          <div class="stat-item" v-if="lesson.minimumScorePercent">
            <i class="fa fa-percent"></i>
            <span>{{ lesson.minimumScorePercent }}% mínimo necessário</span>
          </div>
        </div>

        <div class="quiz-instructions">
          <h4>Instruções:</h4>
          <ul>
            <li>Você terá acesso a todas as questões</li>
            <li>Navegue livremente entre as questões</li>
            <li>Suas respostas serão salvas automaticamente</li>
            <li>Após finalizar, você verá seus resultados</li>
          </ul>
        </div>

        <button 
          class="btn btn-primary btn-lg mt-3" 
          @click="startQuiz"
          :disabled="!canStartQuiz"
        >
          {{ quizData?.hasInProgressAttempt ? 'Continuar Quiz' : 'Iniciar Quiz' }}
        </button>
      </div>
    </div>

    <!-- Quiz Taking Interface -->
    <div v-if="started && !submitted" class="quiz-taking">
      <QuizProgress 
        :current="currentQuestionIndex + 1"
        :total="questions.length"
        :answered="answeredCount"
      />

      <div class="quiz-navigation">
        <button 
          class="btn btn-secondary" 
          @click="previousQuestion"
          :disabled="currentQuestionIndex === 0"
        >
          <i class="fa fa-arrow-left"></i> Anterior
        </button>
        <span class="question-counter">
          Questão {{ currentQuestionIndex + 1 }} de {{ questions.length }}
        </span>
        <button 
          class="btn btn-secondary" 
          @click="nextQuestion"
          :disabled="currentQuestionIndex === questions.length - 1"
        >
          Próxima <i class="fa fa-arrow-right"></i>
        </button>
      </div>

      <QuizQuestion
        v-if="currentQuestion"
        :question="currentQuestion"
        :selected-options="answers[currentQuestion.id] || []"
        @select="handleAnswerSelect"
      />

      <div class="quiz-actions">
        <button 
          class="btn btn-success btn-lg" 
          @click="showSubmitConfirmation = true"
        >
          Finalizar Quiz
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="quiz-loading">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <!-- Submit Confirmation Modal -->
    <div v-if="showSubmitConfirmation" class="modal-overlay" @click.self="showSubmitConfirmation = false">
      <div class="modal-content">
        <h4>Finalizar Quiz?</h4>
        <div v-if="unansweredQuestions.length > 0" class="alert alert-warning">
          <p>Você ainda não respondeu {{ unansweredQuestions.length }} questão(ões):</p>
          <ul>
            <li v-for="q in unansweredQuestions" :key="q.id">
              Questão {{ questions.findIndex(qq => qq.id === q.id) + 1 }}
            </li>
          </ul>
        </div>
        <p>Tem certeza que deseja finalizar o quiz?</p>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="showSubmitConfirmation = false">
            Cancelar
          </button>
          <button class="btn btn-primary" @click="submitQuiz">
            Sim, Finalizar
          </button>
        </div>
      </div>
    </div>

    <!-- Results Screen -->
    <QuizResults
      v-if="submitted && results"
      :results="results"
      :lesson="lesson"
      @try-again="handleTryAgain"
      @next-lesson="handleNextLesson"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import QuizService from '@/services/quiz.service';
import QuizProgress from './QuizProgress.vue';
import QuizQuestion from './QuizQuestion.vue';
import QuizResults from './QuizResults.vue';
import Swal from 'sweetalert2';

const props = defineProps({
  lesson: {
    type: Object,
    required: true,
    default: () => ({
      id: null,
      quizId: null,
      minimumScorePercent: null,
      maxAttempts: 1,
      requiresPreviousQuiz: false
    })
  },
  courseId: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['quizCompleted']);

const loading = ref(true);
const started = ref(false);
const submitted = ref(false);
const quizData = ref(null);
const questions = ref([]);
const answers = ref({});
const currentQuestionIndex = ref(0);
const attemptId = ref(null);
const showSubmitConfirmation = ref(false);
const results = ref(null);
const autoSaveTimeout = ref(null);

const currentQuestion = computed(() => questions.value[currentQuestionIndex.value]);
const answeredCount = computed(() => Object.keys(answers.value).length);
const unansweredQuestions = computed(() => {
  return questions.value.filter(q => !answers.value[q.id] || answers.value[q.id].length === 0);
});
const canStartQuiz = computed(() => {
  return quizData.value && (quizData.value.attemptsRemaining > 0 || quizData.value.hasInProgressAttempt);
});

onMounted(async () => {
  await loadQuizData();
});

const loadQuizData = async () => {
  try {
    loading.value = true;
    const response = await QuizService.getQuizForLesson(props.lesson.id);
    quizData.value = response.data;
    
    // If there's an in-progress attempt, load it
    if (quizData.value.hasInProgressAttempt && quizData.value.attemptId) {
      attemptId.value = quizData.value.attemptId;
      await loadQuestions();
      started.value = true;
    }
  } catch (error) {
    console.error('Error loading quiz:', error);
    Swal.fire('Erro', 'Não foi possível carregar o quiz.', 'error');
  } finally {
    loading.value = false;
  }
};

const startQuiz = async () => {
  try {
    loading.value = true;
    const quizId = props.lesson.quizId || quizData.value?.quiz?.id;
    if (!quizId) {
      Swal.fire('Erro', 'Quiz não encontrado.', 'error');
      return;
    }

    let response;
    if (quizData.value.hasInProgressAttempt && quizData.value.attemptId) {
      // Continue existing attempt
      attemptId.value = quizData.value.attemptId;
      response = { data: { id: attemptId.value } };
    } else {
      // Start new attempt
      response = await QuizService.startAttempt(quizId);
      attemptId.value = response.data.id;
    }

    await loadQuestions();
    started.value = true;
  } catch (error) {
    console.error('Error starting quiz:', error);
    Swal.fire('Erro', 'Não foi possível iniciar o quiz.', 'error');
  } finally {
    loading.value = false;
  }
};

const loadQuestions = async () => {
  if (!attemptId.value) return;
  
  try {
    const response = await QuizService.getAttemptQuestions(attemptId.value);
    questions.value = response.data;
    
    // Load existing answers if any
    // This would need to be implemented based on API response
  } catch (error) {
    console.error('Error loading questions:', error);
  }
};

const handleAnswerSelect = (questionId, optionIds) => {
  answers.value[questionId] = optionIds;
  autoSaveAnswer(questionId, optionIds);
};

const autoSaveAnswer = (questionId, optionIds) => {
  if (autoSaveTimeout.value) {
    clearTimeout(autoSaveTimeout.value);
  }

  autoSaveTimeout.value = setTimeout(async () => {
    if (!attemptId.value) return;
    
    try {
      await QuizService.saveAnswer(attemptId.value, {
        questionId,
        selectedOptionIds: optionIds
      });
      // Show subtle save indicator
      console.log('Answer saved');
    } catch (error) {
      console.error('Error saving answer:', error);
    }
  }, 500);
};

const previousQuestion = () => {
  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value--;
  }
};

const nextQuestion = () => {
  if (currentQuestionIndex.value < questions.value.length - 1) {
    currentQuestionIndex.value++;
  }
};

const submitQuiz = async () => {
  try {
    loading.value = true;
    showSubmitConfirmation.value = false;
    
    if (!attemptId.value) {
      Swal.fire('Erro', 'Tentativa não encontrada.', 'error');
      return;
    }

    const response = await QuizService.submitAttempt(attemptId.value, props.lesson.id);
    results.value = response.data;
    submitted.value = true;
    emit('quizCompleted');
  } catch (error) {
    console.error('Error submitting quiz:', error);
    Swal.fire('Erro', 'Não foi possível finalizar o quiz.', 'error');
  } finally {
    loading.value = false;
  }
};

const handleTryAgain = () => {
  started.value = false;
  submitted.value = false;
  answers.value = {};
  currentQuestionIndex.value = 0;
  results.value = null;
  loadQuizData();
};

const handleNextLesson = () => {
  emit('quizCompleted');
};
</script>

<style scoped>
.quiz-player {
  padding: 20px;
  /* max-width: 1200px; */
  margin: 0 auto;
}

.quiz-start-screen {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.quiz-info-card {
  background: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  max-width: 600px;
  width: 100%;
}

.quiz-stats {
  display: flex;
  gap: 20px;
  margin: 20px 0;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quiz-instructions {
  margin: 20px 0;
  text-align: left;
}

.quiz-instructions ul {
  list-style: disc;
  padding-left: 20px;
}

.quiz-taking {
  background: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.quiz-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.question-counter {
  font-weight: bold;
}

.quiz-actions {
  margin-top: 30px;
  text-align: center;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.quiz-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}
</style>

